import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Target, Eye, Sprout } from "lucide-react";
import "./Home.css";

const Home = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_organic-purity/artifacts/r6j3sknm_GAB%20Organics%20Logo%20without%20BG.png" 
            alt="GAB Organics" 
            className="hero-logo animate-on-scroll"
          />
          <h1 className="hero-title animate-on-scroll">
            Organic You Can Trust.<br />
            <span className="hero-title-line2">Purity You Can Feel.</span>
          </h1>
          <p className="hero-subtitle animate-on-scroll">
            Thoughtfully sourced organic essentials for conscious living.
          </p>
          <div className="hero-buttons animate-on-scroll">
            <Link to="/products" className="btn btn-primary">Explore Our Products</Link>
            <Link to="/contact" className="btn btn-secondary">Contact Us</Link>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section story-section">
        <div className="container">
          <div className="story-content animate-on-scroll">
            <div className="story-text">
              <h2>How It All Started</h2>
              <p>
                GAB Organics was born from a simple yet profound desire to provide families with organic products they can truly trust. Founded in 2022 in Keralam, India, our journey began with a personal commitment to clean, responsible food choices.
              </p>
              <p>
                We understand that every family deserves access to pure, wholesome products—free from compromise, full of integrity. That belief drives everything we do, from selecting our farmers to delivering products to your doorstep.
              </p>
              <p>
                Our story is one of care, responsibility, and conscious living. It's about building trust through transparency and delivering quality that speaks for itself.
              </p>
            </div>
            <div className="story-image">
              <img 
                src="https://images.unsplash.com/photo-1699859955036-fe4d7f173d15?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxzdGFyJTIwYW5pc2UlMjBjYXJkYW1vbXxlbnwwfHx8fDE3NzE2MDg0MTF8MA&ixlib=rb-4.1.0&q=85" 
                alt="Kerala whole spices" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section mission-vision-section">
        <div className="container">
          <div className="mission-vision-grid">
            <div className="mission-card animate-on-scroll">
              <div className="mv-icon">
                <Target size={50} />
              </div>
              <h3>Our Mission</h3>
              <p>
                To provide thoughtfully sourced organic products that families can trust. We believe in making conscious living accessible, authentic, and rooted in integrity.
              </p>
            </div>
            <div className="vision-card animate-on-scroll">
              <div className="mv-icon">
                <Eye size={50} />
              </div>
              <h3>Our Vision</h3>
              <p>
                To grow into a globally trusted organic brand while staying true to our roots. We envision a world where purity, responsibility, and quality are never compromised.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sourcing Philosophy */}
      <section className="section sourcing-section">
        <div className="container">
          <div className="section-title animate-on-scroll">
            <h2>Our Sourcing Philosophy</h2>
            <p>How we ensure quality and purity in every product</p>
          </div>
          <div className="sourcing-content">
            <div className="sourcing-image animate-on-scroll">
              <img 
                src="https://images.pexels.com/photos/28800915/pexels-photo-28800915.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Organic farming" 
              />
            </div>
            <div className="sourcing-text animate-on-scroll">
              <div className="sourcing-point">
                <CheckCircle size={28} />
                <div>
                  <h4>Carefully Selected Farmers</h4>
                  <p>We partner with trusted farmers who share our dedication to organic practices and sustainable agriculture.</p>
                </div>
              </div>
              <div className="sourcing-point">
                <CheckCircle size={28} />
                <div>
                  <h4>EU-Certified Suppliers</h4>
                  <p>Our supply sources meet stringent EU organic certification standards, ensuring uncompromising quality.</p>
                </div>
              </div>
              <div className="sourcing-point">
                <CheckCircle size={28} />
                <div>
                  <h4>Reputed Vendors</h4>
                  <p>Every vendor we work with is thoroughly vetted to guarantee authenticity and reliability.</p>
                </div>
              </div>
              <div className="sourcing-point">
                <CheckCircle size={28} />
                <div>
                  <h4>Quality-First Approach</h4>
                  <p>From farm to family, we never compromise on the purity and integrity of our products.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Profile */}
      <section className="section founder-profile-section">
        <div className="container">
          <div className="founder-profile animate-on-scroll">
            <div className="founder-profile-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_organic-purity/artifacts/67klrg4p_Screenshot%202026-02-20%20at%2010.54.36%E2%80%AFPM.png" 
                alt="Vineeth Abraham Koodathumannil - Founder" 
                className="founder-photo"
              />
            </div>
            <div className="founder-profile-text">
              <h2>Meet the Founder</h2>
              <h3>Vineeth Abraham Koodathumannil</h3>
              <p className="founder-title">Founder, GAB Organics</p>
              
              <div className="founder-bio">
                <p>
                  With a solid background in Microbiology and international professional experience in structured food environments, Vineeth brings both scientific rigor and genuine care to GAB Organics.
                </p>
                <p>
                  Having worked in diverse food industry settings across the globe, Vineeth understands the importance of quality, safety, and integrity in every aspect of food production and sourcing.
                </p>
                <p>
                  With a humble beginning in 2022 in Keralam, India, GAB Organics is Vineeth's personal mission to provide his family—and yours—with organic products that embody the highest standards of purity and trust.
                </p>
                <p>
                  Driven by a deep commitment to clean, responsible food choices, Vineeth personally oversees every aspect of GAB Organics, ensuring that each product meets his exacting standards before it reaches your home.
                </p>
              </div>
              
              <Link to="/contact" className="btn btn-primary">Get in Touch</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
